import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

import {
  registerAuthHandlers,
  setAccessToken,
} from '@/api/client';
import { login as apiLogin, logout as apiLogout, me as apiMe } from '@/api/auth';
import type { User } from '@/types';

/**
 * Auth state lives in memory. The refresh token is an httpOnly cookie set
 * by /api/auth/login — out of JS reach. The access token is held by the
 * api client (also memory only); we mirror the user here for the UI.
 *
 * On mount we call /api/users/me: if there's no token it 401s, the client
 * silently refreshes against the cookie, and we land logged-in across a
 * hard reload. If the cookie is gone, we drop to the login screen.
 */

type Status = 'loading' | 'authed' | 'anon';

interface AuthState {
  user: User | null;
  status: Status;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    registerAuthHandlers({
      onRefreshed: (res) => setUser(res.user),
      onAuthLost: () => {
        setUser(null);
        setStatus('anon');
      },
    });

    let alive = true;
    apiMe()
      .then((u) => {
        if (!alive) return;
        setUser(u);
        setStatus('authed');
      })
      .catch(() => {
        if (!alive) return;
        setStatus('anon');
      });
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    setAccessToken(res.access_token);
    setUser(res.user);
    setStatus('authed');
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setAccessToken(null);
      setUser(null);
      setStatus('anon');
    }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, status, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth outside <AuthProvider>');
  return ctx;
}
